"use client";

import React from "react";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Info } from "lucide-react";

type DispatchValue = "customer" | "customer_and_agent" | "agent";

interface DispatchControlSectionProps {
  dispatchContract: DispatchValue;
  dispatchInvoice: DispatchValue;
  dispatchReminder: DispatchValue;
  dispatchEmail: DispatchValue;
  agentEmails: string[];
  agentDispatchEmail: string;
  onDispatchContractChange: (v: DispatchValue) => void;
  onDispatchInvoiceChange: (v: DispatchValue) => void;
  onDispatchReminderChange: (v: DispatchValue) => void;
  onDispatchEmailChange: (v: DispatchValue) => void;
  onAgentDispatchEmailChange: (email: string) => void;
  agentDispatchEmailError: string; // New prop for error message
  agentDispatchEmailRef: React.RefObject<HTMLDivElement>; // New prop for ref
}

const DispatchControlSection: React.FC<DispatchControlSectionProps> = ({
  dispatchContract,
  dispatchInvoice,
  dispatchReminder,
  dispatchEmail,
  agentEmails,
  agentDispatchEmail,
  onDispatchContractChange,
  onDispatchInvoiceChange,
  onDispatchReminderChange,
  onDispatchEmailChange,
  onAgentDispatchEmailChange,
  agentDispatchEmailError, // Destructure new prop
  agentDispatchEmailRef, // Destructure new prop
}) => {
  const { t } = useTranslation();
  const dispatchOptions = ["customer", "customer_and_agent", "agent"] as const;

  const dispatchOptionLabel = (value: typeof dispatchOptions[number]) => {
    if (value === "customer") return t("broker-display-customer");
    if (value === "customer_and_agent") return t("broker-display-customer-and-agent");
    return t("broker-display-agent");
  };

  return (
    <div className="rounded-md border p-4">
      <div className="mb-3">
        <div className="text-sm font-medium">{t("contactDetails.dispatchControlTitle")}</div>
        <div className="text-xs text-muted-foreground">{t("broker-display-dispatch-control-input-label")}</div>
      </div>
      <div className="grid grid-cols-1 gap-3">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Label htmlFor="dispatchEmail">{t("contactDetails.dispatchEmails")}</Label>
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="ghost" size="icon" className="h-6 w-6">
                  <Info className="h-4 w-4 text-muted-foreground" />
                  <span className="sr-only">{t("common.info")}</span>
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t("contactDetails.dispatchEmailsInfoTitle")}</DialogTitle>
                  <DialogDescription asChild>
                    <div className="space-y-3 text-sm text-muted-foreground">
                      <p>{t("contactDetails.dispatchEmailsInfoIntro")}</p>
                      <div>
                        <p className="font-medium">{t("contactDetails.dispatchEmailsInfoListTitle")}</p>
                        <ul className="list-disc list-inside space-y-1">
                          <li>{t("contactDetails.dispatchEmailsInfoItemOffer")}</li>
                          <li>{t("contactDetails.dispatchEmailsInfoItemConfirmation")}</li>
                          <li>{t("contactDetails.dispatchEmailsInfoItemFollowUp")}</li>
                        </ul>
                      </div>
                    </div>
                  </DialogDescription>
                </DialogHeader>
              </DialogContent>
            </Dialog>
          </div>
          <Select value={dispatchEmail} onValueChange={(v) => onDispatchEmailChange(v as DispatchValue)}>
            <SelectTrigger id="dispatchEmail">
              <SelectValue placeholder={t("contactDetails.selectDispatchPlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              {dispatchOptions.map(opt => (
                <SelectItem key={`email-${opt}`} value={opt}>
                  {dispatchOptionLabel(opt)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="dispatchContract">{t("contactDetails.dispatchContracts")}</Label>
          <Select value={dispatchContract} onValueChange={(v) => onDispatchContractChange(v as DispatchValue)}>
            <SelectTrigger id="dispatchContract">
              <SelectValue placeholder={t("contactDetails.selectDispatchPlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              {dispatchOptions.map(opt => (
                <SelectItem key={`contract-${opt}`} value={opt}>
                  {dispatchOptionLabel(opt)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="dispatchInvoice">{t("contactDetails.dispatchInvoices")}</Label>
          <Select value={dispatchInvoice} onValueChange={(v) => onDispatchInvoiceChange(v as DispatchValue)}>
            <SelectTrigger id="dispatchInvoice">
              <SelectValue placeholder={t("contactDetails.selectDispatchPlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              {dispatchOptions.map(opt => (
                <SelectItem key={`invoice-${opt}`} value={opt}>
                  {dispatchOptionLabel(opt)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="dispatchReminder">{t("contactDetails.dispatchReminders")}</Label>
          <Select value={dispatchReminder} onValueChange={(v) => onDispatchReminderChange(v as DispatchValue)}>
            <SelectTrigger id="dispatchReminder">
              <SelectValue placeholder={t("contactDetails.selectDispatchPlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              {dispatchOptions.map(opt => (
                <SelectItem key={`reminder-${opt}`} value={opt}>
                  {dispatchOptionLabel(opt)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {agentEmails && agentEmails.length > 0 && (
          <div className="space-y-2" ref={agentDispatchEmailRef}> {/* Apply ref here */}
            <Label htmlFor="agentEmail">{t("contactDetails.agentEmailLabel")}</Label>
            <Select
              value={agentDispatchEmail || ""}
              onValueChange={(v) => {
                onAgentDispatchEmailChange(v);
              }}
              // Add conditional styling for error
              className={cn(agentDispatchEmailError && "border-destructive focus-visible:ring-destructive")}
            >
              <SelectTrigger id="agentEmail">
                <SelectValue placeholder={t("contactDetails.chooseAgentEmailPlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                {agentEmails.map(e => (
                  <SelectItem key={e} value={e}>{e}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {agentDispatchEmailError && <p className="text-sm text-destructive">{agentDispatchEmailError}</p>}
          </div>
        )}
      </div>
    </div>
  );
};

export default DispatchControlSection;
